import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Stack,
  CircularProgress,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSnackbar } from 'components/GlobalSnackbar';
import apiReq, { backendHost } from 'core/api';
import { tokenStorage } from 'utils';
import {
  registerProcessEventCallback,
  unregisterProcessEventCallback,
} from 'events/webSocketHandlers';
import { PageShell } from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';

// --- Константы ---

const STEPS = ['Загрузка файла', 'Транскрибация', 'Выбор обработки', 'Результат'];

const STORAGE_KEYS = {
  ACTIVE_STEP: 'summarizer_active_step',
  FILE_NAME: 'summarizer_file_name',
  FILE_SIZE: 'summarizer_file_size',
  FILE_TYPE: 'summarizer_file_type',
  FILE_ID: 'summarizer_file_id',
  TRANSCRIPTION_TEXT: 'summarizer_transcription_text',
  TRANSCRIPTION_FILE_ID: 'summarizer_transcription_file_id',
  PROCESS_TRANSCRIPTION: 'summarizer_process_transcription',
  PROCESS_SUMMARIZATION: 'summarizer_process_summarization',
  PROCESS_AGREEMENT: 'summarizer_process_agreement',
  SELECTED_SUMMARIZATION: 'summarizer_selected_summarization',
  SELECTED_AGREEMENT: 'summarizer_selected_agreement',
};

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

const SUPPORTED_FORMATS =
  '.mp3,.aiff,.dsd,.flac,.aac,.mqa,.ogg,.wav,.alac,.ape,.m4a,.mp4,.avi,.mkv,.mov,.webm,.flv,.wmv,.mpeg,.mpg,audio/*,video/*';

// --- Модуль-уровневый трекер загрузки ---

let activeUpload = {
  promise: null,
  progress: 0,
  listeners: new Set(),
};

const notifyUploadListeners = (progress) => {
  activeUpload.progress = progress;
  activeUpload.listeners.forEach((fn) => fn(progress));
};

const UPLOAD_CONCURRENCY = 4; // параллельных чанков одновременно

const uploadFileChunked = (file, projectId, processType) => {
  if (activeUpload.promise) {
    return activeUpload.promise;
  }

  const uploadPromise = (async () => {
    try {
      const initBody = {
        project_id: projectId,
        name: file.name,
        size: file.size,
        mime_type: file.type || 'application/octet-stream',
      };
      if (processType) {
        initBody.process_type = processType;
      }

      const initData = await apiReq('file/upload/init', 'POST', initBody);
      const { file_id, part_size, parts_total } = initData;

      // Параллельная загрузка чанков с ограничением concurrency
      const results = new Array(parts_total);
      let completedParts = 0;

      const uploadPart = async (partIndex) => {
        const start = partIndex * part_size;
        const end = Math.min(start + part_size, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('file_id', file_id);
        formData.append('part_number', String(partIndex + 1));
        formData.append('chunk', chunk);

        const partData = await apiReq('file/upload/part', 'POST', formData, true);
        results[partIndex] = { part_number: partData.part_number, etag: partData.etag };

        completedParts++;
        notifyUploadListeners(Math.round((completedParts / parts_total) * 100));
      };

      // Пул воркеров: каждый берёт следующий чанк из очереди
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < parts_total) {
          const idx = nextIndex++;
          await uploadPart(idx);
        }
      };

      const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, parts_total) }, () =>
        worker()
      );
      await Promise.all(workers);

      await apiReq('file/upload/complete', 'POST', { file_id, parts: results });

      return file_id;
    } finally {
      activeUpload.promise = null;
      activeUpload.progress = 0;
    }
  })();

  activeUpload.promise = uploadPromise;
  activeUpload.progress = 0;

  return uploadPromise;
};

// --- Утилиты ---

const checkProcessStatus = async (processId) => {
  const data = await apiReq(`process/${processId}/status`, 'GET');
  return data.status;
};

const downloadProcessResult = async (processType, processId) => {
  const token = tokenStorage.get();
  const res = await fetch(`${backendHost}/process/${processType}/${processId}/result`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Ошибка при скачивании результата');
  return await res.text();
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const downloadTextFile = (text, filename) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const PROCESS_STORAGE_MAP = {
  transcription: STORAGE_KEYS.PROCESS_TRANSCRIPTION,
  summarization: STORAGE_KEYS.PROCESS_SUMMARIZATION,
  'agreement-extraction': STORAGE_KEYS.PROCESS_AGREEMENT,
};

// --- Компонент ---

const SummarizerPage = () => {
  const currProject = useSelector((state) => state.reducer.project.id);
  const showSnackbar = useSnackbar();
  const pendingProcessesRef = useRef({});

  // --- Восстановление state из localStorage ---
  const getInitial = (key, fallback) => {
    try {
      const val = localStorage.getItem(key);
      if (val === null) return fallback;
      if (fallback === false || fallback === true) return val === 'true';
      if (typeof fallback === 'number') return parseInt(val, 10) || fallback;
      return val;
    } catch {
      return fallback;
    }
  };

  // Stepper
  const [activeStep, setActiveStep] = useState(() => getInitial(STORAGE_KEYS.ACTIVE_STEP, 0));

  // Шаг 0: загрузка файла
  const [file, setFile] = useState(() => {
    const name = localStorage.getItem(STORAGE_KEYS.FILE_NAME);
    const size = localStorage.getItem(STORAGE_KEYS.FILE_SIZE);
    const type = localStorage.getItem(STORAGE_KEYS.FILE_TYPE);
    return name && size ? { name, size: parseInt(size, 10), type: type || '' } : null;
  });
  const [fileId, setFileId] = useState(() => getInitial(STORAGE_KEYS.FILE_ID, null));
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Шаг 1: транскрибация
  const [transcriptionText, setTranscriptionText] = useState(
    () => getInitial(STORAGE_KEYS.TRANSCRIPTION_TEXT, '') || ''
  );
  const [isTranscriptionLoading, setIsTranscriptionLoading] = useState(false);

  // Шаг 2: повторная загрузка + выбор
  const [isReuploadLoading, setIsReuploadLoading] = useState(false);
  const [reuploadProgress, setReuploadProgress] = useState(0);
  const [selectedSummarization, setSelectedSummarization] = useState(() =>
    getInitial(STORAGE_KEYS.SELECTED_SUMMARIZATION, true)
  );
  const [selectedAgreement, setSelectedAgreement] = useState(() =>
    getInitial(STORAGE_KEYS.SELECTED_AGREEMENT, true)
  );

  // Шаг 3: результаты
  const [summaryText, setSummaryText] = useState('');
  const [minutesText, setMinutesText] = useState('');
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isMinutesLoading, setIsMinutesLoading] = useState(false);

  // --- Сохранение в localStorage ---
  const persist = useCallback((key, value) => {
    try {
      localStorage.setItem(key, String(value));
    } catch {
      // ignore localStorage errors
    }
  }, []);

  const setAndPersistStep = useCallback(
    (step) => {
      setActiveStep(step);
      persist(STORAGE_KEYS.ACTIVE_STEP, step);
    },
    [persist]
  );

  // --- Подписка на прогресс загрузки ---
  useEffect(() => {
    const onProgress = (progress) => setUploadProgress(progress);
    activeUpload.listeners.add(onProgress);
    return () => activeUpload.listeners.delete(onProgress);
  }, []);

  // --- Подхват активной загрузки при монтировании ---
  useEffect(() => {
    if (activeUpload.promise) {
      setIsFileLoading(true);
      setUploadProgress(activeUpload.progress);
      activeUpload.promise
        .then((id) => {
          setFileId(id);
          persist(STORAGE_KEYS.FILE_ID, id);
          setIsFileLoading(false);
        })
        .catch(() => setIsFileLoading(false));
    }
  }, [persist]);

  // --- beforeunload ---
  useEffect(() => {
    const handler = (e) => {
      if (
        activeUpload.promise ||
        isTranscriptionLoading ||
        isReuploadLoading ||
        isSummaryLoading ||
        isMinutesLoading
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isTranscriptionLoading, isReuploadLoading, isSummaryLoading, isMinutesLoading]);

  // --- WebSocket ---
  const waitForProcess = useCallback((processId) => {
    return new Promise((resolve, reject) => {
      pendingProcessesRef.current[processId] = { resolve, reject };
    });
  }, []);

  useEffect(() => {
    const handleProcessEvent = (_eventName, data) => {
      const pending = pendingProcessesRef.current[data.process_id];
      if (!pending) return;
      if (data.status === 'success') {
        pending.resolve(data.process_id);
      } else if (data.status === 'error') {
        pending.reject(new Error(data.message || 'Ошибка обработки'));
      }
      delete pendingProcessesRef.current[data.process_id];
    };
    registerProcessEventCallback(handleProcessEvent);
    return () => unregisterProcessEventCallback();
  }, []);

  // --- Восстановление процессов при монтировании ---
  const resumeProcess = useCallback(
    async (processType, processId) => {
      const status = await checkProcessStatus(processId);
      if (status === 'completed') {
        const text = await downloadProcessResult(processType, processId);
        const storageKey = PROCESS_STORAGE_MAP[processType];
        if (storageKey) localStorage.removeItem(storageKey);
        return text;
      } else if (status === 'processing') {
        await waitForProcess(processId);
        const text = await downloadProcessResult(processType, processId);
        const storageKey = PROCESS_STORAGE_MAP[processType];
        if (storageKey) localStorage.removeItem(storageKey);
        return text;
      } else {
        const storageKey = PROCESS_STORAGE_MAP[processType];
        if (storageKey) localStorage.removeItem(storageKey);
        throw new Error('Процесс завершился с ошибкой');
      }
    },
    [waitForProcess]
  );

  useEffect(() => {
    // Восстановление транскрибации (шаг 1)
    const transcriptionPid = localStorage.getItem(STORAGE_KEYS.PROCESS_TRANSCRIPTION);
    if (transcriptionPid && !transcriptionText) {
      setIsTranscriptionLoading(true);
      resumeProcess('transcription', transcriptionPid)
        .then((text) => {
          setTranscriptionText(text || '');
          persist(STORAGE_KEYS.TRANSCRIPTION_TEXT, text || '');
          if (activeStep < 1) setAndPersistStep(1);
        })
        .catch((err) => {
          console.error(err);
          showSnackbar({ message: 'Ошибка восстановления транскрибации', severity: 'error' });
        })
        .finally(() => setIsTranscriptionLoading(false));
    }

    // Восстановление суммаризации / договорённостей (шаг 3)
    const summaryPid = localStorage.getItem(STORAGE_KEYS.PROCESS_SUMMARIZATION);
    const agreementPid = localStorage.getItem(STORAGE_KEYS.PROCESS_AGREEMENT);

    if (summaryPid) {
      setIsSummaryLoading(true);
      resumeProcess('summarization', summaryPid)
        .then((text) => setSummaryText(text || ''))
        .catch((err) => {
          console.error(err);
          showSnackbar({ message: 'Ошибка восстановления суммаризации', severity: 'error' });
        })
        .finally(() => setIsSummaryLoading(false));
    }
    if (agreementPid) {
      setIsMinutesLoading(true);
      resumeProcess('agreement-extraction', agreementPid)
        .then((text) => setMinutesText(text || ''))
        .catch((err) => {
          console.error(err);
          showSnackbar({ message: 'Ошибка восстановления договорённостей', severity: 'error' });
        })
        .finally(() => setIsMinutesLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Утилиты UI ---
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar({ message: `${label} скопирован в буфер обмена`, severity: 'success' });
    } catch {
      showSnackbar({ message: 'Не удалось скопировать текст', severity: 'error' });
    }
  };

  // --- Шаг 0: Загрузка файла ---
  const handleFileChange = async (event) => {
    if (!event.target.files?.[0]) return;
    const selectedFile = event.target.files[0];

    if (selectedFile.size > MAX_FILE_SIZE) {
      showSnackbar({ message: 'Размер файла не должен превышать 2GB', severity: 'error' });
      return;
    }

    setFile(selectedFile);
    setFileId(null);
    setTranscriptionText('');
    setUploadProgress(0);

    persist(STORAGE_KEYS.FILE_NAME, selectedFile.name);
    persist(STORAGE_KEYS.FILE_SIZE, selectedFile.size);
    persist(STORAGE_KEYS.FILE_TYPE, selectedFile.type || '');
    persist(STORAGE_KEYS.FILE_ID, '');
    persist(STORAGE_KEYS.TRANSCRIPTION_TEXT, '');

    setIsFileLoading(true);

    try {
      const uploadedFileId = await uploadFileChunked(selectedFile, currProject, 'transcription');
      setFileId(uploadedFileId);
      persist(STORAGE_KEYS.FILE_ID, uploadedFileId);
      setIsFileLoading(false);

      // Автоматически запускаем транскрибацию
      setIsTranscriptionLoading(true);
      setAndPersistStep(1);

      const processData = await apiReq('process/transcription', 'POST', {
        file_id: uploadedFileId,
      });
      persist(STORAGE_KEYS.PROCESS_TRANSCRIPTION, processData.process_id);

      await waitForProcess(processData.process_id);
      const text = await downloadProcessResult('transcription', processData.process_id);

      setTranscriptionText(text || '');
      persist(STORAGE_KEYS.TRANSCRIPTION_TEXT, text || '');
      localStorage.removeItem(STORAGE_KEYS.PROCESS_TRANSCRIPTION);
      setIsTranscriptionLoading(false);

      showSnackbar({ message: 'Транскрибация завершена', severity: 'success' });
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: error.message || 'Ошибка при загрузке или транскрибации',
        severity: 'error',
      });
      setIsFileLoading(false);
      setIsTranscriptionLoading(false);
    }
  };

  // --- Загрузка текстового файла на бэк ---
  const uploadTranscriptionText = async (processType, onProgress) => {
    const textBlob = new Blob([transcriptionText], { type: 'text/plain' });
    const textFile = new File([textBlob], 'transcription.txt', { type: 'text/plain' });

    const initData = await apiReq('file/upload/init', 'POST', {
      project_id: currProject,
      name: textFile.name,
      size: textFile.size,
      mime_type: 'text/plain',
      process_type: processType,
    });
    const { file_id, part_size, parts_total } = initData;

    const parts = [];
    for (let i = 0; i < parts_total; i++) {
      const start = i * part_size;
      const end = Math.min(start + part_size, textFile.size);
      const chunk = textFile.slice(start, end);

      const formData = new FormData();
      formData.append('file_id', file_id);
      formData.append('part_number', String(i + 1));
      formData.append('chunk', chunk);

      const partData = await apiReq('file/upload/part', 'POST', formData, true);
      parts.push({ part_number: partData.part_number, etag: partData.etag });
      if (onProgress) onProgress(i + 1, parts_total);
    }

    await apiReq('file/upload/complete', 'POST', { file_id, parts });
    return file_id;
  };

  // --- Шаг 2: Повторная загрузка + запуск обработки ---
  const handleStartProcessing = async () => {
    if (!selectedSummarization && !selectedAgreement) {
      showSnackbar({ message: 'Выберите хотя бы один тип обработки', severity: 'warning' });
      return;
    }

    persist(STORAGE_KEYS.SELECTED_SUMMARIZATION, selectedSummarization);
    persist(STORAGE_KEYS.SELECTED_AGREEMENT, selectedAgreement);

    setIsReuploadLoading(true);
    setReuploadProgress(0);

    try {
      // Загружаем файл отдельно для каждого выбранного процесса
      const typesToUpload = [];
      if (selectedSummarization) typesToUpload.push('summarization');
      if (selectedAgreement) typesToUpload.push('agreement_extraction');

      const fileIds = {};
      let totalUploaded = 0;
      const totalTypes = typesToUpload.length;

      for (const processType of typesToUpload) {
        const typeIndex = typesToUpload.indexOf(processType);
        const fid = await uploadTranscriptionText(processType, (done, total) => {
          const typeProgress = done / total;
          const overallProgress = ((typeIndex + typeProgress) / totalTypes) * 100;
          setReuploadProgress(Math.round(overallProgress));
        });
        fileIds[processType] = fid;
        totalUploaded++;
        setReuploadProgress(Math.round((totalUploaded / totalTypes) * 100));
      }

      setIsReuploadLoading(false);

      // Переходим на шаг 3 и запускаем процессы
      setAndPersistStep(3);

      const promises = [];

      if (selectedSummarization) {
        setIsSummaryLoading(true);
        const p = (async () => {
          const data = await apiReq('process/summarization', 'POST', {
            file_id: fileIds['summarization'],
          });
          persist(STORAGE_KEYS.PROCESS_SUMMARIZATION, data.process_id);
          await waitForProcess(data.process_id);
          const text = await downloadProcessResult('summarization', data.process_id);
          localStorage.removeItem(STORAGE_KEYS.PROCESS_SUMMARIZATION);
          setSummaryText(text || '');
          setIsSummaryLoading(false);
        })();
        promises.push(
          p.catch((err) => {
            console.error(err);
            showSnackbar({ message: 'Ошибка суммаризации', severity: 'error' });
            setIsSummaryLoading(false);
          })
        );
      }

      if (selectedAgreement) {
        setIsMinutesLoading(true);
        const p = (async () => {
          const data = await apiReq('process/agreement-extraction', 'POST', {
            file_id: fileIds['agreement_extraction'],
          });
          persist(STORAGE_KEYS.PROCESS_AGREEMENT, data.process_id);
          await waitForProcess(data.process_id);
          const text = await downloadProcessResult('agreement-extraction', data.process_id);
          localStorage.removeItem(STORAGE_KEYS.PROCESS_AGREEMENT);
          setMinutesText(text || '');
          setIsMinutesLoading(false);
        })();
        promises.push(
          p.catch((err) => {
            console.error(err);
            showSnackbar({ message: 'Ошибка извлечения договорённостей', severity: 'error' });
            setIsMinutesLoading(false);
          })
        );
      }

      await Promise.allSettled(promises);
      showSnackbar({ message: 'Обработка завершена', severity: 'success' });
    } catch (error) {
      console.error(error);
      showSnackbar({ message: error.message || 'Ошибка при обработке', severity: 'error' });
      setIsReuploadLoading(false);
    }
  };

  // --- Сброс ---
  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setFileId(null);
    setIsFileLoading(false);
    setUploadProgress(0);
    setTranscriptionText('');
    setIsTranscriptionLoading(false);
    setIsReuploadLoading(false);
    setReuploadProgress(0);
    setSelectedSummarization(true);
    setSelectedAgreement(true);
    setSummaryText('');
    setMinutesText('');
    setIsSummaryLoading(false);
    setIsMinutesLoading(false);
    pendingProcessesRef.current = {};

    Object.values(STORAGE_KEYS).forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore localStorage errors
      }
    });

    const fileInput = document.getElementById('file-input');
    if (fileInput) fileInput.value = '';

    showSnackbar({ message: 'Процесс сброшен', severity: 'info' });
  };

  // --- Навигация степпера ---
  const canGoToStep = (step) => {
    if (step === 0) return true;
    if (step === 1) return !!fileId || !!transcriptionText;
    if (step === 2) return !!transcriptionText;
    if (step === 3) return !!(summaryText || minutesText || isSummaryLoading || isMinutesLoading);
    return false;
  };

  const handleStepClick = (step) => {
    if (canGoToStep(step)) {
      setAndPersistStep(step);
    }
  };

  const isAnyLoading =
    isFileLoading ||
    isTranscriptionLoading ||
    isReuploadLoading ||
    isSummaryLoading ||
    isMinutesLoading;

  // --- Общие стили ---
  const cardSx = {
    p: 4,
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'rgba(209, 213, 219, 0.8)',
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.06), 0px 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  };

  const loadingCardSx = {
    ...cardSx,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 6,
  };

  // --- Рендер шагов ---

  const renderStep0 = () => (
    <Paper elevation={0} sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background:
            'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 36, color: 'primary.main' }} />
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Загрузите аудио или видео файл
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
        Файл будет транскрибирован в текст. Поддерживаются форматы MP3, MP4, WAV, FLAC, MKV и другие
        (до 2GB).
      </Typography>

      <input
        type="file"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        id="file-input"
        accept={SUPPORTED_FORMATS}
        disabled={isFileLoading || isTranscriptionLoading}
      />
      <label htmlFor="file-input">
        <Button
          component="span"
          variant="contained"
          size="large"
          startIcon={<CloudUploadIcon />}
          disabled={isFileLoading || isTranscriptionLoading}
          sx={{ cursor: 'pointer', px: 4, py: 1.5 }}
        >
          Выбрать файл
        </Button>
      </label>

      {file && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: '10px',
            background: 'rgba(99, 102, 241, 0.04)',
            border: '1px solid rgba(99, 102, 241, 0.12)',
            maxWidth: 400,
            mx: 'auto',
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {file.name}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1 }}>
            <Chip label={formatFileSize(file.size)} size="small" variant="outlined" />
            <Chip label={file.type || 'неизвестно'} size="small" variant="outlined" />
          </Stack>
          {isFileLoading && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Загрузка... {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  borderRadius: 4,
                  height: 6,
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #6366F1, #818CF8)',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          )}
          {isTranscriptionLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={14} color="secondary" />
              <Typography variant="caption" color="secondary.main">
                Транскрибация выполняется...
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Paper>
  );

  const renderStep1 = () => (
    <Stack spacing={3}>
      {isTranscriptionLoading ? (
        <Paper elevation={0} sx={loadingCardSx}>
          <CircularProgress size={48} sx={{ mb: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Транскрибация выполняется
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Это может занять несколько минут в зависимости от размера файла
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'rgba(99, 102, 241, 0.03)',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Результат транскрибации
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(transcriptionText, 'Транскрибация')}
                  disabled={!transcriptionText}
                  sx={{
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'rgba(99, 102, 241, 0.08)',
                    },
                  }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => downloadTextFile(transcriptionText, 'transcription.txt')}
                  disabled={!transcriptionText}
                  sx={{
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'rgba(99, 102, 241, 0.08)',
                    },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
            <Box sx={{ p: 3, maxHeight: 400, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {transcriptionText || 'Нет данных транскрибации'}
              </Typography>
            </Box>
          </Paper>
        </>
      )}
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing={3}>
      {isReuploadLoading ? (
        <Paper elevation={0} sx={loadingCardSx}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Подготовка файла
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Загрузка транскрибации для обработки... {reuploadProgress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={reuploadProgress}
            sx={{
              width: '100%',
              maxWidth: 300,
              borderRadius: 4,
              height: 6,
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #6366F1, #818CF8)',
                borderRadius: 4,
              },
            }}
          />
        </Paper>
      ) : (
        <>
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="h6" gutterBottom>
              Выберите тип обработки
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Текст транскрибации будет автоматически загружен для обработки. Можно выбрать оба
              варианта одновременно.
            </Typography>
            <Stack spacing={1.5}>
              <Paper
                elevation={0}
                onClick={() => setSelectedSummarization(!selectedSummarization)}
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: selectedSummarization
                    ? 'secondary.main'
                    : 'rgba(209, 213, 219, 0.8)',
                  background: selectedSummarization ? 'rgba(236, 72, 153, 0.04)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'secondary.light',
                    transform: 'translateY(-1px)',
                    boxShadow: '0px 4px 12px rgba(236, 72, 153, 0.12)',
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedSummarization}
                      onChange={(e) => setSelectedSummarization(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={600}>Суммаризация</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Краткое содержание текста
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Paper>

              <Paper
                elevation={0}
                onClick={() => setSelectedAgreement(!selectedAgreement)}
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: selectedAgreement ? 'warning.main' : 'rgba(209, 213, 219, 0.8)',
                  background: selectedAgreement ? 'rgba(245, 158, 11, 0.04)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'warning.light',
                    transform: 'translateY(-1px)',
                    boxShadow: '0px 4px 12px rgba(245, 158, 11, 0.12)',
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedAgreement}
                      onChange={(e) => setSelectedAgreement(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={600}>Договорённости</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Извлечение ключевых договорённостей и решений
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Paper>
            </Stack>
          </Paper>

          <Button
            variant="contained"
            size="large"
            onClick={handleStartProcessing}
            disabled={!selectedSummarization && !selectedAgreement}
            fullWidth
            sx={{ py: 1.5 }}
          >
            Запустить обработку
          </Button>
        </>
      )}
    </Stack>
  );

  const renderResultBlock = (title, text, isLoading, color, colorRgba) => (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 0,
        overflow: 'hidden',
        borderColor: text ? `${color}.main` : 'rgba(209, 213, 219, 0.8)',
        borderTopWidth: text ? 3 : 1,
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: colorRgba,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} color={`${color}.dark`}>
          {title}
        </Typography>
        {text && (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(text, title)}
              sx={{ '&:hover': { color: `${color}.main` } }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => downloadTextFile(text, `${title.toLowerCase()}.txt`)}
              sx={{ '&:hover': { color: `${color}.main` } }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Box>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 4,
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={28} color={color} />
          <Typography color="text.secondary">Обработка...</Typography>
        </Box>
      ) : text ? (
        <Box
          sx={{
            p: 3,
            maxHeight: 350,
            overflow: 'auto',
            '& h1': { fontSize: '1.4rem', fontWeight: 700, mt: 2, mb: 1 },
            '& h2': { fontSize: '1.2rem', fontWeight: 600, mt: 2, mb: 1 },
            '& h3': { fontSize: '1.05rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
            '& p': { fontSize: '0.875rem', lineHeight: 1.8, mb: 1 },
            '& ul, & ol': { pl: 3, mb: 1, fontSize: '0.875rem', lineHeight: 1.8 },
            '& li': { mb: 0.5 },
            '& strong': { fontWeight: 600 },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: 'divider',
              pl: 2,
              ml: 0,
              color: 'text.secondary',
              fontStyle: 'italic',
            },
            '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 2 },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.875rem',
              mb: 1,
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              px: 1.5,
              py: 0.75,
              textAlign: 'left',
            },
            '& th': { fontWeight: 600, backgroundColor: 'action.hover' },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Нет данных
        </Typography>
      )}
    </Paper>
  );

  const renderStep3 = () => (
    <Stack spacing={3}>
      {selectedSummarization &&
        renderResultBlock(
          'Суммаризация',
          summaryText,
          isSummaryLoading,
          'secondary',
          'rgba(236, 72, 153, 0.03)'
        )}
      {selectedAgreement &&
        renderResultBlock(
          'Договорённости',
          minutesText,
          isMinutesLoading,
          'warning',
          'rgba(245, 158, 11, 0.03)'
        )}
    </Stack>
  );

  const stepContent = [renderStep0, renderStep1, renderStep2, renderStep3];

  const inner = (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      {!USE_NEW_SHELL && (
        <>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            Саммаризатор
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Транскрибация аудио и видео с последующей обработкой текста
          </Typography>
        </>
      )}

      <Stepper
        activeStep={activeStep}
        sx={{
          mb: 4,
          '& .MuiStepConnector-line': {
            borderColor: 'rgba(99, 102, 241, 0.2)',
          },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
            borderColor: 'primary.main',
          },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: 'primary.main',
          },
        }}
      >
        {STEPS.map((label, index) => (
          <Step
            key={label}
            completed={index < activeStep}
            sx={{
              cursor: canGoToStep(index) ? 'pointer' : 'default',
              '& .MuiStepLabel-root': {
                transition: 'opacity 0.2s ease',
                opacity: canGoToStep(index) ? 1 : 0.5,
              },
            }}
            onClick={() => handleStepClick(index)}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 2, mb: 3 }}>{stepContent[activeStep]()}</Box>

      {/* Навигация */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Box>
          {activeStep > 0 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setAndPersistStep(activeStep - 1)}
              disabled={isAnyLoading}
              sx={{
                '&:hover': { transform: 'translateX(-2px)' },
                transition: 'transform 0.2s ease',
              }}
            >
              Назад
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {(file || activeStep > 0) && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleReset}
            >
              Сбросить
            </Button>
          )}

          {activeStep === 1 && transcriptionText && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setAndPersistStep(2)}
              disabled={isTranscriptionLoading}
              sx={{ px: 4 }}
            >
              Далее
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );

  if (USE_NEW_SHELL) {
    return (
      <PageShell
        title="Саммаризатор"
        subtitle="Транскрибация аудио и видео с последующей обработкой текста"
      >
        {inner}
      </PageShell>
    );
  }
  return inner;
};

export default SummarizerPage;
